import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import { reconcileQueuedTransaction } from './queries'
import { setOnSent } from './queue-document'
import { registerServiceWorker } from './sw-register'
import './index.css'

const container = document.getElementById('root')
if (!container) throw new Error('#root is missing from index.html')

/**
 * One client for the whole app.
 *
 * Reads are not retried. Every named refusal this API makes — you are not in
 * that group, that group does not exist, you are not signed in — is a
 * settled answer, and asking three more times produces the same one more
 * slowly. The write path has its own answer to a lost reply, and it is the
 * `Idempotency-Key`, not a retry loop that cannot tell "never arrived" from
 * "never came back".
 */
const client = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    },
  },
})

/**
 * The other end of `src/queue.ts`: what a queued write earns once it finally
 * reaches the server. It is wired here, once, rather than inside
 * `src/queue-document.ts` itself, so that module never has to import
 * `src/queries.ts` — the queue does not need to know what TanStack Query is
 * to hold a write, only to be told what to do when one lands.
 */
setOnSent((write, body) => reconcileQueuedTransaction(client, write, body))

registerServiceWorker()

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={client}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
)
